import { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, Search, Printer, Settings, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import FlowLoader from "../FlowLoader";
import FilterPills from "./FilterPills";
import PrinterSelectModal from "../PrinterSelectModal";
import { printCourierPalletLabel, type PalletPrint } from "../helpers/courierPalletPrint";
import "../../styles/admin-outbound.css";

// Admin › Warehouse › Outbound — one day's loading at the conveyor unload
// point (Assistant Team Leader and above, Alex 2026-10-14; it used to sit
// on the handheld page and operators could see it).
//
// Per courier: parcels shipped (order_label rows for the warehouse-local
// day) vs loaded (a pallet match at the unload point). Below that, ONE
// list of every parcel and every problem scan, filterable by courier and
// status and searchable by order or tracking number, and the Palletways
// pallets released or shipped that day.
//
// A "Pallets" view pill (Alex, 2026-10-19) swaps the parcel list for the
// day's courier pallets — the team leader prints each closed pallet's 6x4
// label from there, so it is one click from the top of the page rather
// than a table under everything else.

type SummaryCarrier = {
    carrier_code: string; carrier_name: string; shipped: number; loaded: number; loaded_other_day: number;
    not_loaded: number; mismatches: number; set_aside: number; voided: number; duplicate: number; carrier_scanned: number;
};
type SummaryEvent = {
    id: number; created_at: string; barcode: string; order_number: string | null; carrier_code: string | null; sort_code?: string | null;
    carrier_name: string | null; verdict: string; verdict_reason: string | null; outcome: string | null;
    bay_name: string | null; bay_carrier_code: string | null; bay_carrier_name: string | null; scanned_by_name: string | null;
};
// order_id / order_number are null for a Newport parcel: nothing is booked
// through the Portal there, so the courier was read off the label's shape
// and the scan itself is the parcel (tracking_number = the label key).
type Parcel = {
    tracking_number: string; order_id: string | null; order_number: string | null; carrier_code: string; box_position: number;
    shipped_at: string; packed_by_name: string | null; desk_id: string | null; label_status: string;
    loaded_at: string | null; loaded_bay: string | null; loaded_by: string | null; sort_code: string | null;
    // The physical pallet (migration 225) the parcel was loaded on and
    // whether that pallet has gone onto the lorry.
    pallet_label?: string | null; pallet_status?: "open" | "closed" | "loaded" | null; pallet_loaded_at?: string | null;
    set_aside_at: string | null; set_aside_by: string | null; mismatches: number;
    status: "loaded" | "not_loaded" | "set_aside";
};
// A physical pallet: opened by the first parcel on a position, closed under
// a pre-printed label at the unload point, loaded by the forklift driver.
export type CourierPallet = {
    id: number; label: string | null; status: "open" | "closed" | "loaded"; carrier_code: string; carrier_name: string;
    bay_barcode: string; bay_name: string | null; parcel_count: number | null; sequence_gap: boolean;
    opened_at: string | null; closed_at: string | null; closed_by: string | null; loaded_at: string | null; loaded_by: string | null;
    label_confirmed_at?: string | null;
    // What the 6x4 label says (null while open). The team leader prints it
    // from this page (Alex, 2026-10-19: no auto-print on the handheld yet).
    print?: PalletPrint | null;
};
type ShippedPallet = {
    order_id: string; order_number: string; state: "booked" | "released" | "shipped"; pallets: number | null;
    pallet_types: string[]; con_no: string | null; tracking_id: string | null; manifested_at: string | null;
    manifested_by: string | null; booked_by_name: string | null; updated_at: string;
};
export type OutboundSummaryData = {
    warehouse: string; date: string; carriers: SummaryCarrier[]; unknown_labels: number;
    parcels?: Parcel[]; pallets?: ShippedPallet[]; courier_pallets?: CourierPallet[]; events: SummaryEvent[];
};

// One row of the unified list: a shipped parcel, or a problem scan that
// is not itself a shipped parcel (a void / duplicate / unknown label, or a
// wrong-pallet scan shown as its own line so the pallet it hit is visible).
export type Tag = "loaded" | "not_loaded" | "set_aside" | "wrong_pallet" | "voided" | "duplicate" | "unknown_label" | "wrong_warehouse";
export const TAGS: Tag[] = ["loaded", "not_loaded", "set_aside", "wrong_pallet", "voided", "duplicate", "unknown_label", "wrong_warehouse"];
type Row = {
    key: string; ts: string; order: string; tracking: string; carrier_code: string | null; sort_code: string | null;
    tag: Tag; detail: string; by: string | null;
};

export const carrierClass = (code: string | null | undefined): string => {
    const c = String(code ?? "").toLowerCase();
    return ["apc", "inpost", "dpd", "cargus", "palletways", "raben", "royal_mail", "collection"].includes(c) ? `unload-carrier--${c}` : "unload-carrier--other";
};

const fmtTime = (iso: string | null): string => {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

type T = (key: string, vars?: Record<string, string>) => string;

const eventTag = (e: SummaryEvent): Tag | null => {
    if (e.outcome === "mismatch") return "wrong_pallet";
    if (e.outcome === "set_aside") return null;               // shown on the parcel's own row
    if (e.verdict === "voided") return "voided";
    if (e.verdict === "duplicate") return "duplicate";
    if (e.verdict === "unknown_label") return "unknown_label";
    if (e.verdict === "wrong_warehouse") return "wrong_warehouse";
    return null;
};

// Build the unified, filterable list.
export const buildRows = (data: OutboundSummaryData, carrierNames: Map<string, string>, t: T): Row[] => {
    const rows: Row[] = [];
    for (const p of data.parcels ?? []) {
        const desk = p.desk_id ? ` · ${p.desk_id}` : "";
        const onLorry = p.pallet_status === "loaded" ? ` · ${t("outbound_detail_on_lorry", { time: fmtTime(p.pallet_loaded_at ?? null) })}` : "";
        const detail = p.status === "loaded"
            ? (p.pallet_label
                ? t("outbound_detail_loaded_pallet", { bay: p.loaded_bay ?? "", pallet: p.pallet_label, time: fmtTime(p.loaded_at) }) + onLorry
                : t("outbound_detail_loaded", { bay: p.loaded_bay ?? "", time: fmtTime(p.loaded_at) }))
            : p.status === "set_aside"
                ? t("outbound_detail_set_aside", { time: fmtTime(p.set_aside_at) })
                : t("outbound_detail_not_loaded", { time: fmtTime(p.shipped_at), position: String(p.box_position), desk });
        rows.push({
            key: `parcel:${p.tracking_number}`,
            ts: p.loaded_at ?? p.set_aside_at ?? p.shipped_at,
            order: p.order_number ?? "", tracking: p.tracking_number,
            carrier_code: p.carrier_code, sort_code: p.sort_code,
            tag: p.status, detail,
            by: p.status === "loaded" ? p.loaded_by : p.status === "set_aside" ? p.set_aside_by : p.packed_by_name,
        });
    }
    for (const e of data.events) {
        const tag = eventTag(e);
        if (!tag) continue;
        const expected = [carrierNames.get(e.carrier_code ?? "") ?? e.carrier_name ?? "", e.sort_code ?? ""].filter(Boolean).join(" ");
        const detail = tag === "wrong_pallet"
            ? t("outbound_detail_mismatch", { bay: e.bay_name ?? "", expected })
            : e.verdict_reason ? `${t(`outbound_tag_${tag}`)} · ${e.verdict_reason}` : t(`outbound_tag_${tag}`);
        rows.push({
            key: `event:${e.id}`, ts: e.created_at,
            order: e.order_number ?? "", tracking: e.barcode,
            carrier_code: e.carrier_code, sort_code: e.sort_code ?? null,
            tag, detail, by: e.scanned_by_name,
        });
    }
    rows.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
    return rows;
};

export const filterRows = (rows: Row[], courier: string, tag: string, search: string): Row[] => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
        if (courier && (r.carrier_code ?? "") !== courier) return false;
        if (tag && r.tag !== tag) return false;
        if (q && !r.order.toLowerCase().includes(q) && !r.tracking.toLowerCase().includes(q)) return false;
        return true;
    });
};

const OutboundSummary = ({ warehouse }: { warehouse: string }) => {
    const { t } = useTranslation();
    const [date, setDate] = useState<string>("");
    const [summary, setSummary] = useState<OutboundSummaryData | null>(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState<"parcels" | "pallets">("parcels");
    const [courier, setCourier] = useState("");
    const [tag, setTag] = useState("");
    const [search, setSearch] = useState("");
    const [printingId, setPrintingId] = useState<number | null>(null);
    const [showPrinterSelect, setShowPrinterSelect] = useState(false);

    // Print a closed pallet's 6x4 label (sku, classifier, barcode, date).
    const printPallet = async (p: CourierPallet) => {
        if (!p.print) return;
        setPrintingId(p.id);
        try {
            const ok = await printCourierPalletLabel(p.print, p.closed_at);
            toast.dismiss();
            if (ok) toast.success(t("outbound_printed", { sku: p.print.sku }));
            else toast.error(t("outbound_print_failed"));
        } finally {
            setPrintingId(null);
        }
    };

    const load = useCallback(async (d: string) => {
        setLoading(true);
        try {
            const qs = `warehouse=${encodeURIComponent(warehouse)}${d ? `&date=${d}` : ""}`;
            const res = await apiFetch(`/dispatch/unload/summary?${qs}`, {});
            if (!res.ok) throw new Error("summary");
            const data: OutboundSummaryData = await res.json();
            setSummary(data);
            if (!d) setDate(data.date);
        } catch {
            toast.dismiss(); toast.error(t("server_error"));
        } finally {
            setLoading(false);
        }
    }, [warehouse, t]);

    useEffect(() => { load(""); }, [load]);

    const carriers = summary?.carriers ?? [];
    const pallets = summary?.pallets ?? [];
    const courierPallets = summary?.courier_pallets ?? [];
    const carrierNames = useMemo(() => new Map(carriers.map((c) => [c.carrier_code, c.carrier_name])), [carriers]);
    const rows = useMemo(() => (summary ? buildRows(summary, carrierNames, t) : []), [summary, carrierNames, t]);
    const visible = useMemo(() => filterRows(rows, courier, tag, search), [rows, courier, tag, search]);

    // Courier pills: every courier seen on parcels or problem scans, plus
    // Palletways when pallets left that day.
    const courierOptions = useMemo(() => {
        const codes = new Map<string, string>();
        for (const c of carriers) codes.set(c.carrier_code, c.carrier_name);
        for (const r of rows) if (r.carrier_code && !codes.has(r.carrier_code)) codes.set(r.carrier_code, r.carrier_code.toUpperCase());
        if (pallets.length > 0 && !codes.has("palletways")) codes.set("palletways", "Palletways");
        return [...codes.entries()].map(([value, label]) => ({
            value, label,
            count: value === "palletways" && !carriers.some((c) => c.carrier_code === "palletways")
                ? pallets.length
                : rows.filter((r) => r.carrier_code === value).length,
        }));
    }, [carriers, rows, pallets]);
    const tagOptions = useMemo(() => TAGS.map((value) => ({
        value, label: t(`outbound_tag_${value}`),
        count: filterRows(rows, courier, value, "").length,
    })).filter((o) => o.count > 0 || o.value === "loaded" || o.value === "not_loaded"), [rows, courier, t]);

    const visibleCarriers = courier ? carriers.filter((c) => c.carrier_code === courier) : carriers;
    const visibleCourierPallets = courierPallets.filter((p) => {
        if (courier && p.carrier_code !== courier) return false;
        const q = search.trim().toLowerCase();
        return !q || (p.label ?? "").toLowerCase().includes(q) || (p.bay_name ?? "").toLowerCase().includes(q) || (p.print?.sku ?? "").toLowerCase().includes(q);
    });
    const visiblePallets = (courier && courier !== "palletways") ? [] : pallets.filter((p) => {
        const q = search.trim().toLowerCase();
        return !q || p.order_number.toLowerCase().includes(q) || (p.tracking_id ?? "").toLowerCase().includes(q) || (p.con_no ?? "").toLowerCase().includes(q);
    });

    return (
        <div className="outbound-summary">
            <div className="outbound-toolbar">
                <label className="unload-summary-date">
                    {t("unload_summary_date")}
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => { setDate(e.target.value); if (e.target.value) load(e.target.value); }}
                    />
                </label>
                <label className="outbound-search">
                    <Search size={14} />
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t("outbound_search_placeholder")}
                        aria-label={t("outbound_search_placeholder")}
                    />
                </label>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => load(date)} disabled={loading} aria-label={t("refresh")}>
                    <RefreshCw size={14} className={loading ? "unload-spin" : undefined} />
                    {t("refresh")}
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowPrinterSelect(true)}>
                    <Settings size={14} />
                    {t("printer_select")}
                </button>
            </div>

            {loading && !summary && <FlowLoader />}

            {summary && (
                <>
                    <FilterPills
                        labelKey="outbound_view"
                        hideAll
                        options={[
                            { value: "parcels", label: t("outbound_view_parcels"), count: rows.length },
                            { value: "pallets", label: t("outbound_view_pallets"), count: courierPallets.length },
                        ]}
                        value={view}
                        onChange={(v) => setView(v === "pallets" ? "pallets" : "parcels")}
                        className="outbound-pills"
                    />
                    <FilterPills
                        labelKey="outbound_filter_courier"
                        options={courierOptions}
                        value={courier}
                        onChange={(v) => setCourier(v)}
                        allCount={view === "pallets" ? courierPallets.length : rows.length}
                        className="outbound-pills"
                    />
                    {view === "parcels" && (
                        <FilterPills
                            labelKey="outbound_filter_status"
                            options={tagOptions}
                            value={tag}
                            onChange={setTag}
                            allCount={filterRows(rows, courier, "", "").length}
                            className="outbound-pills"
                        />
                    )}

                    {view === "pallets" && (
                        <>
                            <h2 className="unload-summary-heading">{t("outbound_courier_pallets_title")}</h2>
                            {visibleCourierPallets.length === 0 ? (
                                <p className="unload-summary-empty">{t("outbound_courier_pallets_empty")}</p>
                            ) : (
                                <table className="unload-summary-table" aria-label={t("outbound_courier_pallets_title")}>
                                    <thead>
                                        <tr>
                                            <th>{t("outbound_col_pallet")}</th>
                                            <th>{t("outbound_col_courier")}</th>
                                            <th>{t("outbound_col_position")}</th>
                                            <th>{t("outbound_col_parcels")}</th>
                                            <th>{t("outbound_col_status")}</th>
                                            <th>{t("outbound_col_closed")}</th>
                                            <th>{t("outbound_col_loaded")}</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleCourierPallets.map((p) => (
                                            <tr key={p.id}>
                                                <td>
                                                    <strong>{p.print?.sku ?? p.label ?? "—"}</strong>
                                                    {p.label && <div className="outbound-tracking">{p.label}</div>}
                                                </td>
                                                <td><span className={`unload-session-chip ${carrierClass(p.carrier_code)}`}>{p.carrier_name}</span></td>
                                                <td>{p.bay_name ?? p.bay_barcode}</td>
                                                <td>{p.parcel_count ?? 0}</td>
                                                <td><span className={`outbound-tag outbound-tag--pallet-${p.status}`}>{t(`outbound_pallet_${p.status}`)}</span></td>
                                                <td className="unload-summary-muted">{p.closed_at ? `${fmtTime(p.closed_at)}${p.closed_by ? ` · ${p.closed_by}` : ""}` : "—"}</td>
                                                <td className="unload-summary-muted">{p.loaded_at ? `${fmtTime(p.loaded_at)}${p.loaded_by ? ` · ${p.loaded_by}` : ""}` : "—"}</td>
                                                <td>
                                                    {p.print && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary dispatch-modal-btn"
                                                            onClick={() => printPallet(p)}
                                                            disabled={printingId !== null}
                                                            aria-label={`${t("outbound_print_label")} ${p.print.sku}`}
                                                        >
                                                            {printingId === p.id ? <Loader2 size={14} className="unload-spin" /> : <Printer size={14} />}
                                                            {t("outbound_print_label")}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </>
                    )}

                    {view === "parcels" && (<>
                    <h2 className="unload-summary-heading">{t("unload_summary_title")}</h2>
                    {visibleCarriers.length === 0 ? (
                        <p className="unload-summary-empty">{t("unload_summary_empty")}</p>
                    ) : (
                        <table className="unload-summary-table" aria-label={t("unload_summary_title")}>
                            <thead>
                                <tr>
                                    <th>{t("unload_summary_courier")}</th>
                                    <th>{t("unload_summary_shipped")}</th>
                                    <th>{t("unload_summary_loaded")}</th>
                                    <th>{t("unload_summary_not_loaded")}</th>
                                    <th>{t("unload_summary_mismatches")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleCarriers.map((c) => (
                                    <tr key={c.carrier_code}>
                                        <td><span className={`unload-session-chip ${carrierClass(c.carrier_code)}`}>{c.carrier_name}</span></td>
                                        <td>{c.shipped}</td>
                                        <td>{c.loaded}{c.loaded_other_day > 0 ? ` (+${c.loaded_other_day})` : ""}</td>
                                        <td className={c.not_loaded > 0 ? "unload-summary-bad" : "unload-summary-good"}>{c.not_loaded}</td>
                                        <td className={c.mismatches > 0 ? "unload-summary-bad" : ""}>{c.mismatches}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {summary.unknown_labels > 0 && !courier && (
                        <p className="unload-summary-note">{t("unload_summary_unknown", { count: String(summary.unknown_labels) })}</p>
                    )}

                    <h2 className="unload-summary-heading">{t("outbound_parcels_title")}</h2>
                    {visible.length === 0 ? (
                        <p className="unload-summary-empty">{t("outbound_rows_empty")}</p>
                    ) : (
                        <table className="unload-summary-table outbound-rows" aria-label={t("outbound_parcels_title")}>
                            <thead>
                                <tr>
                                    <th>{t("outbound_col_time")}</th>
                                    <th>{t("outbound_col_order")}</th>
                                    <th>{t("outbound_col_courier")}</th>
                                    <th>{t("outbound_col_code")}</th>
                                    <th>{t("outbound_col_status")}</th>
                                    <th>{t("outbound_col_detail")}</th>
                                    <th>{t("outbound_col_by")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map((r) => (
                                    <tr key={r.key}>
                                        <td className="unload-summary-muted">{fmtTime(r.ts)}</td>
                                        <td><strong>{r.order || "—"}</strong><div className="outbound-tracking">{r.tracking}</div></td>
                                        <td>{r.carrier_code && <span className={`unload-session-chip ${carrierClass(r.carrier_code)}`}>{carrierNames.get(r.carrier_code) ?? r.carrier_code.toUpperCase()}</span>}</td>
                                        <td>{r.sort_code ?? "—"}</td>
                                        <td><span className={`outbound-tag outbound-tag--${r.tag}`}>{t(`outbound_tag_${r.tag}`)}</span></td>
                                        <td>{r.detail}</td>
                                        <td className="unload-summary-muted">{r.by ?? ""}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {(!courier || courier === "palletways") && (
                        <>
                            <h2 className="unload-summary-heading">{t("outbound_pallets_title")}</h2>
                            {visiblePallets.length === 0 ? (
                                <p className="unload-summary-empty">{t("outbound_pallets_empty")}</p>
                            ) : (
                                <table className="unload-summary-table" aria-label={t("outbound_pallets_title")}>
                                    <thead>
                                        <tr>
                                            <th>{t("order")}</th>
                                            <th>{t("outbound_pallets_count")}</th>
                                            <th>{t("outbound_pallets_state")}</th>
                                            <th>{t("outbound_pallets_consignment")}</th>
                                            <th>{t("outbound_pallets_released")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visiblePallets.map((p) => (
                                            <tr key={p.order_id}>
                                                <td><strong>{p.order_number}</strong></td>
                                                <td>{p.pallets ?? "—"}{p.pallet_types.length > 0 ? ` (${p.pallet_types.join(", ")})` : ""}</td>
                                                <td>{t(`outbound_pallet_state_${p.state}`)}</td>
                                                <td className="unload-summary-muted">{p.tracking_id ?? p.con_no ?? "—"}</td>
                                                <td className="unload-summary-muted">
                                                    {fmtTime(p.manifested_at ?? p.updated_at)}{p.manifested_by ? ` · ${p.manifested_by}` : (p.booked_by_name ? ` · ${p.booked_by_name}` : "")}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </>
                    )}
                    </>)}
                </>
            )}
            {showPrinterSelect && (
                <PrinterSelectModal
                    autoPrint={true}
                    printMode={null}
                    onSelect={() => setShowPrinterSelect(false)}
                    onConnectLocal={() => setShowPrinterSelect(false)}
                    onDisconnect={() => setShowPrinterSelect(false)}
                    onClose={() => setShowPrinterSelect(false)}
                />
            )}
        </div>
    );
};

export default OutboundSummary;
